import { useState, useEffect, useRef } from "react";
import { useSearchParams } from "react-router";
import Container from "@/components/ui/container";
import { useBrands, usePlan, type PlanDetails } from "@/modules/brand";
import { useAuth } from "@/modules/auth";
import { PLAN_GOLD, usePlans, type Plan } from "@/modules/plan";
import { partnerApi, type Partner } from "@/modules/partner";
import Skeleton from "@/components/skeleton";
import { useOnboardingStore } from "@/modules/onboarding/stores/onboarding-store";
import { PLAN_STARTER, PLAN_GROWTH, PLAN_PLATINUM, PLAN_STARTER_LEGACY } from "@/modules/plan";

function formatDate(dateString: string): string {
	return new Date(dateString).toLocaleDateString("en-US", {
		month: "long",
		day: "numeric",
		year: "numeric",
	});
}

function PlanCard({ plan, recommended }: { plan: Plan; recommended?: boolean; standalone?: boolean }) {
	const isLifetime = plan.interval === "lifetime";
	const isGrowth = plan.slug.startsWith("growth");
	const isStarter = !isGrowth && !isLifetime;
	const description = isGrowth ? "For growing businesses" : isStarter ? "For getting started" : "Pay once, use forever with all future updates";
	const features = isStarter ? PLAN_STARTER : isGrowth ? PLAN_GROWTH : isLifetime ? PLAN_PLATINUM : null;

	return (
		<div className={`relative bg-white rounded-xl border-2 p-6 flex flex-col ${recommended ? "border-brand shadow-lg" : "border-gray-200"}`}>
			{recommended && (
				<div className="absolute -top-3 left-1/2 -translate-x-1/2">
					<span className="bg-brand text-white text-xs font-semibold px-3 py-1 rounded-full">Recommended</span>
				</div>
			)}

			<div className="mb-4">
				<h3 className="text-lg font-semibold text-primary">{plan.name}</h3>
				<p className="text-sm text-tertiary mt-1">{description}</p>
			</div>

			<div className="mb-6">
				<span className="text-3xl font-bold text-primary">${plan.price}</span>
				<span className="text-tertiary ml-1">{isLifetime ? " one-time" : "/month"}</span>
			</div>

			<ul className="space-y-3 flex-1">
				{features &&
					features
						.filter((f) => f.planCardVisibilty === true)
						.map((feature, index) => (
							<li key={index} className="flex items-start gap-2 text-sm text-secondary">
								{feature.type !== "title" && (
									<svg className="w-5 h-5 text-green-500 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
										<path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
									</svg>
								)}
								<span>{feature.text}</span>
							</li>
						))}
			</ul>
		</div>
	);
}

function PlanCardSkeleton() {
	return (
		<div className="bg-white rounded-xl border-2 border-gray-200 p-6 flex flex-col">
			<div className="mb-4">
				<Skeleton width={100} height={24} />
				<Skeleton width={200} height={16} className="mt-2" />
			</div>
			<div className="mb-6">
				<Skeleton width={80} height={36} />
			</div>
			<div className="space-y-3 flex-1">
				{[1, 2, 3, 4].map((i) => (
					<Skeleton key={i} width="100%" height={20} />
				))}
			</div>
		</div>
	);
}

// Trial State Component
function BillingTrialState({ trialEndsAt, isExpired, workspaceId, partner }: { trialEndsAt?: string | null; isExpired?: boolean; workspaceId: string; partner?: Partner | null }) {
	const [checkoutError, setCheckoutError] = useState<string | null>(null);
	const [loadingPlanId, setLoadingPlanId] = useState<string | null>(null);
	const [couponCopied, setCouponCopied] = useState(false);
	const [verifiedPartner, setVerifiedPartner] = useState<Partner | null | undefined>(undefined);
	const { plans, isLoading: isPlansLoading, isCheckoutLoading, indexPlans, createCheckout } = usePlans();

	// Verify partner is still active by re-fetching from the partner API (skip if trial expired)
	useEffect(() => {
		if (isExpired || !partner?.slug) {
			setVerifiedPartner(null);
			return;
		}
		partnerApi.getBySlug(partner.slug).then((result) => {
			setVerifiedPartner(result.success && result.data ? result.data : null);
		});
	}, [partner?.slug, isExpired]);

	// Partner discounts only apply during active trial
	const activePartner = isExpired ? null : (verifiedPartner ?? null);
	const isPartnerPending = !isExpired && !!partner?.slug && verifiedPartner === undefined;
	const isLoading = isPlansLoading || isPartnerPending;

	useEffect(() => {
		if (!plans) {
			indexPlans();
		}
	}, [plans, indexPlans]);

	// Filter plans based on partner's available_plans setting
	const filteredPlans = activePartner?.available_plans === "lifetime" ? (plans || []).filter((p) => p.interval === "lifetime") : plans || [];

	// Sort plans: Starter Monthly, Growth Monthly, Growth Lifetime
	const sortedPlans = [...filteredPlans].sort((a, b) => {
		// Starter first
		if (a.slug.startsWith("starter") && !b.slug.startsWith("starter")) return -1;
		if (!a.slug.startsWith("starter") && b.slug.startsWith("starter")) return 1;
		// Then monthly before lifetime
		if (a.interval === "monthly" && b.interval === "lifetime") return -1;
		if (a.interval === "lifetime" && b.interval === "monthly") return 1;
		return 0;
	});

	// When partner restricts to specific plans, send the plan_id so checkout only shows that plan
	const restrictedPlanId = activePartner?.available_plans === "lifetime" && sortedPlans.length === 1 ? sortedPlans[0]?.id : undefined;

	const handleUpgrade = async () => {
		setCheckoutError(null);
		setLoadingPlanId("upgrade");

		const result = await createCheckout(workspaceId, restrictedPlanId);

		if (result?.checkout_session_url) {
			useOnboardingStore.getState().completeStep(workspaceId, "remove_branding");
			import("@/lib/analytics").then(({ trackEvent }) => {
				trackEvent("begin_checkout", {}, "InitiateCheckout");
			});
			window.location.href = result.checkout_session_url;
		} else {
			setCheckoutError("Failed to create checkout session. Please try again.");
			setLoadingPlanId(null);
		}
	};

	if (isLoading) {
		return (
			<div className="space-y-6">
				<Skeleton width="100%" height={100} className="rounded-xl" />
				<Skeleton width={200} height={24} />
				<Skeleton width={280} height={16} />
				<div className="grid grid-cols-1 md:grid-cols-3 gap-6">
					<PlanCardSkeleton />
					<PlanCardSkeleton />
					<PlanCardSkeleton />
				</div>
			</div>
		);
	}

	return (
		<>
			{/* Trial Status Banner */}
			{isExpired ? (
				<div className="bg-red-50 border border-red-200 rounded-xl p-6 mb-8">
					<div className="flex items-start gap-4">
						<div className="shrink-0 w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
							<svg className="w-6 h-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
								<path
									strokeLinecap="round"
									strokeLinejoin="round"
									d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
								/>
							</svg>
						</div>
						<div>
							<h2 className="text-lg font-semibold text-red-800">Your trial has expired</h2>
							<p className="text-red-700 mt-1">
								{trialEndsAt
									? `Your trial expired on ${formatDate(trialEndsAt)}. Subscribe now to continue using Say About Us and access all your testimonials.`
									: "Your trial has ended. Subscribe now to continue using Say About Us and access all your testimonials."}
							</p>
						</div>
					</div>
				</div>
			) : (
				<div className="bg-amber-50 border border-amber-200 rounded-xl p-6 mb-8">
					<div className="flex items-start gap-4">
						<div className="shrink-0 w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center">
							<svg className="w-6 h-6 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
								<path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
							</svg>
						</div>
						<div>
							<h2 className="text-lg font-semibold text-amber-800">You're on a free trial</h2>
							<p className="text-amber-700 mt-1">
								{trialEndsAt
									? `Your trial ends on ${formatDate(trialEndsAt)}. Subscribe to a plan to continue using all features.`
									: "Subscribe to a plan to continue using all features after your trial ends."}
							</p>
						</div>
					</div>
				</div>
			)}

			{/* Plan Selection */}
			<div>
				<div className="mb-6">
					<h2 className="text-lg font-semibold text-primary">Choose your plan</h2>
					<p className="text-sm text-tertiary mt-1">Select the plan that works best for you</p>
				</div>

				{/* Checkout Error */}
				{checkoutError && (
					<div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
						<div className="flex items-center gap-2 text-red-800">
							<svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
								<path
									strokeLinecap="round"
									strokeLinejoin="round"
									d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
								/>
							</svg>
							<span className="text-sm font-medium">{checkoutError}</span>
						</div>
					</div>
				)}

				{/* Plans Grid */}
				<div className={`grid grid-cols-1 ${sortedPlans.length === 1 ? "md:grid-cols-1 max-w-md" : sortedPlans.length === 2 ? "md:grid-cols-2 max-w-2xl" : "md:grid-cols-3"} gap-6`}>
					{isLoading ? (
						<>
							<PlanCardSkeleton />
							<PlanCardSkeleton />
							<PlanCardSkeleton />
						</>
					) : (
						sortedPlans.map((plan) => <PlanCard key={plan.id} plan={plan} recommended={plan.slug === "growth-lifetime"} standalone={sortedPlans.length === 1} />)
					)}
				</div>

				{/* Upgrade / Continue Checkout Banner */}
				{!isLoading &&
					(() => {
						const hasPartnerCoupon = !!activePartner?.coupon_code;
						const isDark = activePartner?.brand_color_scheme === "dark";
						const textColor = hasPartnerCoupon ? (isDark ? "#ffffff" : "#1a1a1a") : undefined;
						const subtleTextColor = hasPartnerCoupon ? (isDark ? "rgba(255,255,255,0.8)" : "rgba(0,0,0,0.65)") : undefined;
						const borderColor = hasPartnerCoupon ? (isDark ? "rgba(0,0,0,0.2)" : "rgba(0,0,0,0.1)") : undefined;
						const codeBg = hasPartnerCoupon ? (isDark ? "rgba(255,255,255,0.15)" : "rgba(255,255,255,0.7)") : undefined;
						const codeBorder = hasPartnerCoupon ? (isDark ? "rgba(255,255,255,0.3)" : "rgba(0,0,0,0.15)") : undefined;
						const btnBg = hasPartnerCoupon ? (isDark ? "rgba(255,255,255,0.2)" : "rgba(0,0,0,0.8)") : undefined;
						const btnText = hasPartnerCoupon ? (isDark ? "#ffffff" : "#ffffff") : undefined;

						return (
							<div
								className={`mt-8 rounded-xl p-5 ${hasPartnerCoupon ? "" : "bg-white border border-gray-200"}`}
								style={
									hasPartnerCoupon
										? {
												backgroundColor: activePartner!.brand_color,
												color: textColor,
												border: `1px solid ${borderColor}`,
											}
										: undefined
								}
							>
								<div className="flex items-center justify-between gap-4">
									<div className="flex items-center gap-3">
										{hasPartnerCoupon && activePartner!.logo_url && <img src={activePartner!.logo_url} alt={activePartner!.name} className="h-8 object-contain flex-shrink-0" />}
										<div>
											{hasPartnerCoupon ? (
												<>
													<h3 className="text-sm font-semibold">{activePartner!.offer_title || `Special offer through ${activePartner!.name}`}</h3>
													<p className="text-sm mt-0.5" style={{ color: subtleTextColor }}>
														{activePartner!.offer_description ||
															(activePartner!.available_plans === "all"
																? "Copy the code and apply it at checkout to claim your discount."
																: "Coupon will be auto-applied during checkout.")}
													</p>
												</>
											) : (
												<>
													<h3 className="text-sm font-semibold text-primary">Ready to upgrade?</h3>
													<p className="text-sm text-tertiary mt-0.5">You can select a plan on the next page before making payment.</p>
												</>
											)}
										</div>
									</div>
									<div className="shrink-0 flex items-center gap-3">
										{hasPartnerCoupon && (
											<button
												type="button"
												onClick={() => {
													navigator.clipboard.writeText(activePartner!.coupon_code!);
													setCouponCopied(true);
													setTimeout(() => setCouponCopied(false), 2000);
												}}
												className="flex items-center gap-2 rounded-lg border border-dashed px-4 py-2 cursor-pointer transition-opacity hover:opacity-80"
												style={{ borderColor: codeBorder, backgroundColor: codeBg }}
											>
												<span className="text-sm font-mono font-semibold tracking-wide" style={{ color: textColor }}>
													{activePartner!.coupon_code}
												</span>
												{couponCopied ? (
													<svg className="w-4 h-4 shrink-0" style={{ color: textColor }} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
														<path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
													</svg>
												) : (
													<svg className="w-4 h-4 shrink-0" style={{ color: textColor, opacity: 0.7 }} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
														<path
															strokeLinecap="round"
															strokeLinejoin="round"
															d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
														/>
													</svg>
												)}
											</button>
										)}
										<button
											onClick={handleUpgrade}
											disabled={isCheckoutLoading}
											className={
												hasPartnerCoupon
													? "px-5 py-2.5 font-medium text-sm rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
													: "px-5 py-2.5 bg-brand text-white font-medium text-sm rounded-lg hover:bg-brand/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
											}
											style={hasPartnerCoupon ? { backgroundColor: btnBg, color: btnText } : undefined}
										>
											{isCheckoutLoading && loadingPlanId === "upgrade" ? (
												<span className="flex items-center justify-center gap-2">
													<svg className="w-4 h-4 animate-spin" viewBox="0 0 24 24" fill="none">
														<circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" />
														<path
															className="opacity-75"
															fill="currentColor"
															d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
														/>
													</svg>
													Redirecting...
												</span>
											) : (
												"Upgrade Now"
											)}
										</button>
									</div>
								</div>
							</div>
						);
					})()}
			</div>
		</>
	);
}

// Stater State Component
function BillingFreeState({
	planDetails,
	isExpired,
	workspaceId,
	partner,
}: {
	planDetails: PlanDetails | null;
	trialEndsAt?: string | null;
	isExpired?: boolean;
	workspaceId: string;
	partner?: Partner | null;
}) {
	const [checkoutError, setCheckoutError] = useState<string | null>(null);
	const [loadingPlanId, setLoadingPlanId] = useState<string | null>(null);
	const [couponCopied, setCouponCopied] = useState(false);
	const [verifiedPartner] = useState<Partner | null | undefined>(undefined);
	const { plans, isLoading: isPlansLoading, isCheckoutLoading, indexPlans, createCheckout } = usePlans();

	const displayPlanName = planDetails?.plan.name || "—";
	console.log(planDetails);

	// Partner discounts only apply during active trial
	const activePartner = isExpired ? null : (verifiedPartner ?? null);
	const isPartnerPending = !isExpired && !!partner?.slug && verifiedPartner === undefined;
	const isLoading = isPlansLoading || isPartnerPending;

	useEffect(() => {
		if (!plans) {
			indexPlans();
		}
	}, [plans, indexPlans]);

	// Filter plans based on partner's available_plans setting
	const filteredPlans = activePartner?.available_plans === "lifetime" ? (plans || []).filter((p) => p.interval === "lifetime") : plans?.filter((p) => p.id !== planDetails?.plan.id) || [];

	// Sort plans: Starter Monthly, Growth Monthly, Growth Lifetime
	const sortedPlans = [...filteredPlans].sort((a, b) => {
		// Starter first
		if (a.slug.startsWith("starter") && !b.slug.startsWith("starter")) return -1;
		if (!a.slug.startsWith("starter") && b.slug.startsWith("starter")) return 1;
		// Then monthly before lifetime
		if (a.interval === "monthly" && b.interval === "lifetime") return -1;
		if (a.interval === "lifetime" && b.interval === "monthly") return 1;
		return 0;
	});

	// When partner restricts to specific plans, send the plan_id so checkout only shows that plan
	const restrictedPlanId = activePartner?.available_plans === "lifetime" && sortedPlans.length === 1 ? sortedPlans[0]?.id : undefined;

	const handleUpgrade = async () => {
		setCheckoutError(null);
		setLoadingPlanId("upgrade");

		const result = await createCheckout(workspaceId, restrictedPlanId);

		if (result?.checkout_session_url) {
			useOnboardingStore.getState().completeStep(workspaceId, "remove_branding");
			import("@/lib/analytics").then(({ trackEvent }) => {
				trackEvent("begin_checkout", {}, "InitiateCheckout");
			});
			window.location.href = result.checkout_session_url;
		} else {
			setCheckoutError("Failed to create checkout session. Please try again.");
			setLoadingPlanId(null);
		}
	};

	if (isLoading) {
		return (
			<div className="space-y-6">
				<Skeleton width="100%" height={100} className="rounded-xl" />
				<Skeleton width={200} height={24} />
				<Skeleton width={280} height={16} />
				<div className="grid grid-cols-1 md:grid-cols-3 gap-6">
					<PlanCardSkeleton />
					<PlanCardSkeleton />
					<PlanCardSkeleton />
				</div>
			</div>
		);
	}

	return (
		<>
			{/* Current Plan Card */}
			<div className="bg-white rounded-xl border border-gray-200 overflow-hidden mb-8">
				<div className="px-6 py-4 border-b border-gray-100 bg-gray-50">
					<h2 className="text-base font-semibold text-primary">Current Plan</h2>
				</div>
				<div className="p-6">
					<div className="flex items-start justify-between">
						<div>
							<div className="flex items-center gap-3">
								<h3 className="text-xl font-semibold text-primary">{displayPlanName}</h3>
								<span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">Active</span>
							</div>
						</div>
						<span className="text-2xl font-bold text-primary">Free</span>
					</div>
				</div>
			</div>

			{/* Plan Features */}
			{planDetails?.plan && (
				<div className="bg-white rounded-xl border border-gray-200 overflow-hidden mb-8">
					<div className="px-6 py-4 border-b border-gray-100 bg-gray-50">
						<h2 className="text-base font-semibold text-primary">Plan Features</h2>
					</div>
					<div className="p-6">
						<ul className="grid grid-cols-1 md:grid-cols-2 gap-3">
							{PLAN_STARTER.map((feature, index) => (
								<li key={index} className="flex items-center gap-2 text-sm text-secondary">
									{feature.type === "feature" && (
										<svg className="w-5 h-5 text-green-500 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
											<path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
										</svg>
									)}
									{feature.type === "info" && (
										<svg className="w-5 h-5 text-gray-400 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
											<path strokeLinecap="round" strokeLinejoin="round" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
										</svg>
									)}
									<span>{feature.text}</span>
								</li>
							))}
						</ul>
					</div>
				</div>
			)}

			{/* Plan Selection */}
			<div>
				<div className="mb-6">
					<h2 className="text-lg font-semibold text-primary">Upgrade your plan</h2>
					<p className="text-sm text-tertiary mt-1">Select the plan that works best for you</p>
				</div>

				{/* Checkout Error */}
				{checkoutError && (
					<div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
						<div className="flex items-center gap-2 text-red-800">
							<svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
								<path
									strokeLinecap="round"
									strokeLinejoin="round"
									d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
								/>
							</svg>
							<span className="text-sm font-medium">{checkoutError}</span>
						</div>
					</div>
				)}

				{/* Plans Grid */}
				<div className={`grid grid-cols-1 ${sortedPlans.length === 1 ? "md:grid-cols-1 max-w-md" : sortedPlans.length === 2 ? "md:grid-cols-2 max-w-2xl" : "md:grid-cols-3"} gap-6`}>
					{isLoading ? (
						<>
							<PlanCardSkeleton />
							<PlanCardSkeleton />
							<PlanCardSkeleton />
						</>
					) : (
						sortedPlans.map((plan) => <PlanCard key={plan.id} plan={plan} recommended={plan.slug === "growth-lifetime"} standalone={sortedPlans.length === 1} />)
					)}
				</div>

				{/* Upgrade / Continue Checkout Banner */}
				{!isLoading &&
					(() => {
						const hasPartnerCoupon = !!activePartner?.coupon_code;
						const isDark = activePartner?.brand_color_scheme === "dark";
						const textColor = hasPartnerCoupon ? (isDark ? "#ffffff" : "#1a1a1a") : undefined;
						const subtleTextColor = hasPartnerCoupon ? (isDark ? "rgba(255,255,255,0.8)" : "rgba(0,0,0,0.65)") : undefined;
						const borderColor = hasPartnerCoupon ? (isDark ? "rgba(0,0,0,0.2)" : "rgba(0,0,0,0.1)") : undefined;
						const codeBg = hasPartnerCoupon ? (isDark ? "rgba(255,255,255,0.15)" : "rgba(255,255,255,0.7)") : undefined;
						const codeBorder = hasPartnerCoupon ? (isDark ? "rgba(255,255,255,0.3)" : "rgba(0,0,0,0.15)") : undefined;
						const btnBg = hasPartnerCoupon ? (isDark ? "rgba(255,255,255,0.2)" : "rgba(0,0,0,0.8)") : undefined;
						const btnText = hasPartnerCoupon ? (isDark ? "#ffffff" : "#ffffff") : undefined;

						return (
							<div
								className={`mt-8 rounded-xl p-5 ${hasPartnerCoupon ? "" : "bg-white border border-gray-200"}`}
								style={
									hasPartnerCoupon
										? {
												backgroundColor: activePartner!.brand_color,
												color: textColor,
												border: `1px solid ${borderColor}`,
											}
										: undefined
								}
							>
								<div className="flex items-center justify-between gap-4">
									<div className="flex items-center gap-3">
										{hasPartnerCoupon && activePartner!.logo_url && <img src={activePartner!.logo_url} alt={activePartner!.name} className="h-8 object-contain flex-shrink-0" />}
										<div>
											{hasPartnerCoupon ? (
												<>
													<h3 className="text-sm font-semibold">{activePartner!.offer_title || `Special offer through ${activePartner!.name}`}</h3>
													<p className="text-sm mt-0.5" style={{ color: subtleTextColor }}>
														{activePartner!.offer_description ||
															(activePartner!.available_plans === "all"
																? "Copy the code and apply it at checkout to claim your discount."
																: "Coupon will be auto-applied during checkout.")}
													</p>
												</>
											) : (
												<>
													<h3 className="text-sm font-semibold text-primary">Ready to upgrade?</h3>
													<p className="text-sm text-tertiary mt-0.5">You can select a plan on the next page before making payment.</p>
												</>
											)}
										</div>
									</div>
									<div className="shrink-0 flex items-center gap-3">
										{hasPartnerCoupon && (
											<button
												type="button"
												onClick={() => {
													navigator.clipboard.writeText(activePartner!.coupon_code!);
													setCouponCopied(true);
													setTimeout(() => setCouponCopied(false), 2000);
												}}
												className="flex items-center gap-2 rounded-lg border border-dashed px-4 py-2 cursor-pointer transition-opacity hover:opacity-80"
												style={{ borderColor: codeBorder, backgroundColor: codeBg }}
											>
												<span className="text-sm font-mono font-semibold tracking-wide" style={{ color: textColor }}>
													{activePartner!.coupon_code}
												</span>
												{couponCopied ? (
													<svg className="w-4 h-4 shrink-0" style={{ color: textColor }} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
														<path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
													</svg>
												) : (
													<svg className="w-4 h-4 shrink-0" style={{ color: textColor, opacity: 0.7 }} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
														<path
															strokeLinecap="round"
															strokeLinejoin="round"
															d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
														/>
													</svg>
												)}
											</button>
										)}
										<button
											onClick={handleUpgrade}
											disabled={isCheckoutLoading}
											className={
												hasPartnerCoupon
													? "px-5 py-2.5 font-medium text-sm rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
													: "px-5 py-2.5 bg-brand text-white font-medium text-sm rounded-lg hover:bg-brand/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
											}
											style={hasPartnerCoupon ? { backgroundColor: btnBg, color: btnText } : undefined}
										>
											{isCheckoutLoading && loadingPlanId === "upgrade" ? (
												<span className="flex items-center justify-center gap-2">
													<svg className="w-4 h-4 animate-spin" viewBox="0 0 24 24" fill="none">
														<circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" />
														<path
															className="opacity-75"
															fill="currentColor"
															d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
														/>
													</svg>
													Redirecting...
												</span>
											) : (
												"Upgrade Now"
											)}
										</button>
									</div>
								</div>
							</div>
						);
					})()}
			</div>
		</>
	);
}

// Active Subscription State Component
function BillingActiveState({
	planDetails,
	subscriptionStartsAt,
	subscriptionRenewsAt,
	workspaceId,
}: {
	planDetails?: PlanDetails | null;
	subscriptionStartsAt?: string | null;
	subscriptionRenewsAt?: string | null;
	workspaceId: string;
}) {
	const [isLoadingPortal, setIsLoadingPortal] = useState(false);
	const { getBillingPortal } = usePlans();

	const handleManageSubscription = async () => {
		setIsLoadingPortal(true);
		const result = await getBillingPortal(workspaceId);
		if (result?.portal_url) {
			window.open(result.portal_url, "_blank");
		}
		setIsLoadingPortal(false);
	};

	const displayPlanName = planDetails?.plan.name || "—";
	const displayPrice = planDetails?.plan?.price;
	const isMonthly = planDetails?.plan.interval === "monthly";
	const planSlug = planDetails?.plan?.slug;
	const features = planSlug === "starter-legacy" ? PLAN_STARTER_LEGACY : PLAN_GROWTH;

	return (
		<div className="space-y-6">
			{/* Current Plan Card */}
			<div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
				<div className="px-6 py-4 border-b border-gray-100 bg-gray-50">
					<h2 className="text-base font-semibold text-primary">Current Plan</h2>
				</div>
				<div className="p-6">
					<div className="flex items-start justify-between">
						<div>
							<div className="flex items-center gap-3">
								<h3 className="text-xl font-semibold text-primary">{displayPlanName}</h3>
								<span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">Active</span>
							</div>
							<p className="text-sm text-tertiary mt-1">{planDetails?.plan ? (isMonthly ? "Billed monthly" : "One-time purchase") : "—"}</p>
						</div>
						<div className="text-right">
							<span className="text-2xl font-bold text-primary">{displayPrice !== undefined ? `$${displayPrice}` : "—"}</span>
							{isMonthly && <span className="text-tertiary">/month</span>}
						</div>
					</div>

					{/* Subscription Details */}
					<div className="mt-6 pt-6 border-t border-gray-100 grid grid-cols-2 gap-6">
						{subscriptionStartsAt && (
							<div>
								<p className="text-xs font-medium text-tertiary uppercase tracking-wide">Started</p>
								<p className="text-sm text-secondary mt-1">{formatDate(subscriptionStartsAt)}</p>
							</div>
						)}
						{isMonthly && subscriptionRenewsAt && (
							<div>
								<p className="text-xs font-medium text-tertiary uppercase tracking-wide">Next billing date</p>
								<p className="text-sm text-secondary mt-1">{formatDate(subscriptionRenewsAt)}</p>
							</div>
						)}
					</div>
				</div>
			</div>

			{/* Plan Features */}
			{planDetails?.plan && (
				<div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
					<div className="px-6 py-4 border-b border-gray-100 bg-gray-50">
						<h2 className="text-base font-semibold text-primary">Plan Features</h2>
					</div>
					<div className="p-6">
						<ul className="grid grid-cols-1 md:grid-cols-2 gap-3">
							{features
								.filter((f) => f.type !== "title")
								.map((feature, index) => (
									<li key={index} className="flex items-center gap-2 text-sm text-secondary">
										{feature.type === "feature" && (
											<svg className="w-5 h-5 text-green-500 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
												<path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
											</svg>
										)}
										{feature.type === "info" && (
											<svg className="w-5 h-5 text-gray-400 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
												<path strokeLinecap="round" strokeLinejoin="round" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
											</svg>
										)}
										<span>{feature.text}</span>
									</li>
								))}
						</ul>
					</div>
				</div>
			)}

			{/* Manage Subscription */}
			{isMonthly && (
				<div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
					<div className="px-6 py-4 border-b border-gray-100 bg-gray-50">
						<h2 className="text-base font-semibold text-primary">Manage Subscription</h2>
					</div>
					<div className="p-6">
						<p className="text-sm text-tertiary mb-4">Update your payment method, view billing history, or cancel your subscription.</p>
						<button
							onClick={handleManageSubscription}
							disabled={isLoadingPortal}
							className="inline-flex items-center gap-2 px-4 py-2 bg-gray-900 text-white text-sm font-medium rounded-lg hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
						>
							{isLoadingPortal ? (
								<>
									<svg className="w-4 h-4 animate-spin" viewBox="0 0 24 24" fill="none">
										<circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" />
										<path
											className="opacity-75"
											fill="currentColor"
											d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
										/>
									</svg>
									Redirecting...
								</>
							) : (
								<>
									<svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
										<path
											strokeLinecap="round"
											strokeLinejoin="round"
											d="M10.343 3.94c.09-.542.56-.94 1.11-.94h1.093c.55 0 1.02.398 1.11.94l.149.894c.07.424.384.764.78.93.398.164.855.142 1.205-.108l.737-.527a1.125 1.125 0 011.45.12l.773.774c.39.389.44 1.002.12 1.45l-.527.737c-.25.35-.272.806-.107 1.204.165.397.505.71.93.78l.893.15c.543.09.94.56.94 1.109v1.094c0 .55-.397 1.02-.94 1.11l-.893.149c-.425.07-.765.383-.93.78-.165.398-.143.854.107 1.204l.527.738c.32.447.269 1.06-.12 1.45l-.774.773a1.125 1.125 0 01-1.449.12l-.738-.527c-.35-.25-.806-.272-1.203-.107-.397.165-.71.505-.781.929l-.149.894c-.09.542-.56.94-1.11.94h-1.094c-.55 0-1.019-.398-1.11-.94l-.148-.894c-.071-.424-.384-.764-.781-.93-.398-.164-.854-.142-1.204.108l-.738.527c-.447.32-1.06.269-1.45-.12l-.773-.774a1.125 1.125 0 01-.12-1.45l.527-.737c.25-.35.273-.806.108-1.204-.165-.397-.505-.71-.93-.78l-.894-.15c-.542-.09-.94-.56-.94-1.109v-1.094c0-.55.398-1.02.94-1.11l.894-.149c.424-.07.765-.383.93-.78.165-.398.143-.854-.107-1.204l-.527-.738a1.125 1.125 0 01.12-1.45l.773-.773a1.125 1.125 0 011.45-.12l.737.527c.35.25.807.272 1.204.107.397-.165.71-.505.78-.929l.15-.894z"
										/>
										<path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
									</svg>
									Manage Subscription
								</>
							)}
						</button>
					</div>
				</div>
			)}
		</div>
	);
}

// Lifetime Subscription State Component
function BillingLifetimeState({ planDetails, subscriptionStartsAt, workspaceId }: { planDetails?: PlanDetails | null; subscriptionStartsAt?: string | null; workspaceId: string }) {
	const [isLoadingPortal, setIsLoadingPortal] = useState(false);
	const { getBillingPortal } = usePlans();

	const handleManageBilling = async () => {
		setIsLoadingPortal(true);
		const result = await getBillingPortal(workspaceId);
		if (result?.portal_url) {
			window.open(result.portal_url, "_blank");
		}
		setIsLoadingPortal(false);
	};

	const displayPlanName = planDetails?.plan?.name || "—";
	const displayPrice = planDetails?.plan?.price;
	const features = planDetails?.plan?.slug === "platinum" ? PLAN_PLATINUM : PLAN_GOLD;

	return (
		<div className="space-y-6">
			{/* Lifetime Plan Card */}
			<div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
				<div className="px-6 py-4 border-b border-gray-100 bg-gray-50">
					<h2 className="text-base font-semibold text-primary">Current Plan</h2>
				</div>
				<div className="p-6">
					<div className="flex items-start justify-between">
						<div>
							<div className="flex items-center gap-3">
								<h3 className="text-xl font-semibold text-primary">{displayPlanName}</h3>
								<span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
									<svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
										<path
											strokeLinecap="round"
											strokeLinejoin="round"
											d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z"
										/>
									</svg>
									Lifetime
								</span>
							</div>
							<p className="text-sm text-tertiary mt-1">{planDetails?.plan ? "One-time purchase • Forever access" : "—"}</p>
						</div>
						<div className="text-right">
							<span className="text-2xl font-bold text-primary">{displayPrice !== undefined ? `$${displayPrice}` : "—"}</span>
							<p className="text-xs text-tertiary mt-1">one-time</p>
						</div>
					</div>

					{/* Purchase Details */}
					{subscriptionStartsAt && (
						<div className="mt-6 pt-6 border-t border-gray-100">
							<div>
								<p className="text-xs font-medium text-tertiary uppercase tracking-wide">Purchased</p>
								<p className="text-sm text-secondary mt-1">{formatDate(subscriptionStartsAt)}</p>
							</div>
						</div>
					)}
				</div>
			</div>

			{/* Lifetime Benefits */}
			<div className="bg-gradient-to-br from-purple-50 to-indigo-50 rounded-xl border border-purple-100 p-6">
				<div className="flex items-start gap-4">
					<div className="shrink-0 w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
						<svg className="w-5 h-5 text-purple-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
							<path
								strokeLinecap="round"
								strokeLinejoin="round"
								d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
							/>
						</svg>
					</div>
					<div>
						<h3 className="text-sm font-semibold text-purple-900">You have lifetime access</h3>
						<p className="text-sm text-purple-700 mt-1">Enjoy all features forever with no recurring payments. Thank you for your support!</p>
					</div>
				</div>
			</div>

			{/* Plan Features */}
			{planDetails?.plan && (
				<div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
					<div className="px-6 py-4 border-b border-gray-100 bg-gray-50">
						<h2 className="text-base font-semibold text-primary">Plan Features</h2>
					</div>
					<div className="p-6">
						<ul className="grid grid-cols-1 md:grid-cols-2 gap-3">
							{features
								.filter((f) => f.type !== "title")
								.map((feature, index) => (
									<li key={index} className="flex items-center gap-2 text-sm text-secondary">
										{feature.type === "feature" && (
											<svg className="w-5 h-5 text-green-500 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
												<path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
											</svg>
										)}
										{feature.type === "info" && (
											<svg className="w-5 h-5 text-gray-400 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
												<path strokeLinecap="round" strokeLinejoin="round" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
											</svg>
										)}
										<span>{feature.text}</span>
									</li>
								))}
						</ul>
					</div>
				</div>
			)}

			{/* Billing Portal */}
			<div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
				<div className="px-6 py-4 border-b border-gray-100 bg-gray-50">
					<h2 className="text-base font-semibold text-primary">Billing</h2>
				</div>
				<div className="p-6">
					<p className="text-sm text-tertiary mb-4">View your payment history and download invoices.</p>
					<button
						onClick={handleManageBilling}
						disabled={isLoadingPortal}
						className="inline-flex items-center gap-2 px-4 py-2 bg-gray-900 text-white text-sm font-medium rounded-lg hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
					>
						{isLoadingPortal ? (
							<>
								<svg className="w-4 h-4 animate-spin" viewBox="0 0 24 24" fill="none">
									<circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" />
									<path
										className="opacity-75"
										fill="currentColor"
										d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
									/>
								</svg>
								Opening...
							</>
						) : (
							<>
								<svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
									<path
										strokeLinecap="round"
										strokeLinejoin="round"
										d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
									/>
								</svg>
								View Invoices
							</>
						)}
					</button>
				</div>
			</div>
		</div>
	);
}

export default function BillingPage() {
	const [searchParams, setSearchParams] = useSearchParams();
	const { currentWorkspaceId } = useAuth();
	const { selectedBrand, viewBrand } = useBrands();
	const [showSuccessMessage, setShowSuccessMessage] = useState(false);
	const [isProcessingPayment, setIsProcessingPayment] = useState(false);
	const [paymentError, setPaymentError] = useState<string | null>(null);
	const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);
	const pollCountRef = useRef(0);
	const pollingStartedRef = useRef(false);
	const { planDetails } = usePlan();

	// Fetch full workspace details including plan
	useEffect(() => {
		if (currentWorkspaceId && !isProcessingPayment) {
			viewBrand(currentWorkspaceId);
		}
	}, [currentWorkspaceId, viewBrand, isProcessingPayment]);

	// Handle payment flow completion - poll for subscription update
	useEffect(() => {
		const paymentFlow = searchParams.get("payment_flow");

		if (paymentFlow === "completed" && currentWorkspaceId && !pollingStartedRef.current) {
			// Mark polling as started to prevent re-triggering
			pollingStartedRef.current = true;

			// Start polling for subscription update
			setIsProcessingPayment(true);
			setPaymentError(null);
			pollCountRef.current = 0;

			const pollSubscriptionStatus = async () => {
				pollCountRef.current += 1;

				const brand = await viewBrand(currentWorkspaceId);

				// First check checkout session status
				const checkoutStatus = brand?.checkout_session_status;

				if (checkoutStatus === "failed" || checkoutStatus === "cancelled") {
					// Payment failed - stop polling and show error
					setIsProcessingPayment(false);
					setPaymentError("Your payment could not be processed. Please try again.");
					pollingStartedRef.current = false;
					setSearchParams({});

					if (pollingRef.current) {
						clearInterval(pollingRef.current);
						pollingRef.current = null;
					}
					return;
				}

				// If checkout succeeded, check subscription status
				if (checkoutStatus === "succeeded") {
					if (brand?.subscription_status && !brand.subscription_status.startsWith("trial")) {
						// Subscription is now active - stop polling
						setIsProcessingPayment(false);
						setShowSuccessMessage(true);
						pollingStartedRef.current = false;
						setSearchParams({});

						// Fire purchase event once per session
						const txnKey = `purchase_tracked_${currentWorkspaceId}`;
						if (!sessionStorage.getItem(txnKey)) {
							sessionStorage.setItem(txnKey, "1");
							import("@/lib/analytics").then(({ trackEvent }) => {
								trackEvent("purchase", {}, "Purchase");
							});
						}

						// Hide success message after 5 seconds
						setTimeout(() => setShowSuccessMessage(false), 5000);

						if (pollingRef.current) {
							clearInterval(pollingRef.current);
							pollingRef.current = null;
						}
						return;
					}
				}

				// Timeout after 30 attempts (60 seconds)
				if (pollCountRef.current >= 30) {
					setIsProcessingPayment(false);
					setPaymentError("Payment is being processed. Please refresh the page in a few moments.");
					pollingStartedRef.current = false;
					setSearchParams({});

					if (pollingRef.current) {
						clearInterval(pollingRef.current);
						pollingRef.current = null;
					}
				}
			};

			// Poll immediately, then every 2 seconds
			pollSubscriptionStatus();
			pollingRef.current = setInterval(pollSubscriptionStatus, 2000);

			return () => {
				if (pollingRef.current) {
					clearInterval(pollingRef.current);
					pollingRef.current = null;
				}
			};
		}
	}, [searchParams, setSearchParams, currentWorkspaceId, viewBrand]);

	// Cleanup polling on unmount
	useEffect(() => {
		return () => {
			if (pollingRef.current) {
				clearInterval(pollingRef.current);
			}
		};
	}, []);

	// For now, always show trial state for preview
	// TODO: Switch based on selectedBrand?.subscription_status
	const subscriptionStatus = selectedBrand?.subscription_status || "trial_active";
	const isBrandLoading = !selectedBrand && !isProcessingPayment;

	return (
		<div className="py-12">
			<Container>
				{/* Loading state - wait for brand data before rendering anything */}
				{isBrandLoading ? (
					<div className="space-y-6">
						<div className="mb-8">
							<Skeleton width={100} height={28} />
							<Skeleton width={300} height={16} className="mt-2" />
						</div>
						<Skeleton width="100%" height={100} className="rounded-xl" />
						<Skeleton width={200} height={24} />
						<div className="grid grid-cols-1 md:grid-cols-3 gap-6">
							<PlanCardSkeleton />
							<PlanCardSkeleton />
							<PlanCardSkeleton />
						</div>
					</div>
				) : isProcessingPayment ? (
					<div className="flex flex-col items-center justify-center py-20">
						<div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-6">
							<svg className="w-8 h-8 text-blue-600 animate-spin" viewBox="0 0 24 24" fill="none">
								<circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" />
								<path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
							</svg>
						</div>
						<h2 className="text-xl font-semibold text-primary mb-2">Processing your payment...</h2>
						<p className="text-tertiary text-center max-w-md">Please wait while we confirm your subscription. This usually takes just a few seconds.</p>
					</div>
				) : (
					<>
						{/* Payment Error */}
						{paymentError && (
							<div className="mb-6 bg-amber-50 border border-amber-200 rounded-xl p-4">
								<div className="flex items-center gap-3">
									<div className="shrink-0 w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center">
										<svg className="w-5 h-5 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
											<path
												strokeLinecap="round"
												strokeLinejoin="round"
												d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
											/>
										</svg>
									</div>
									<div>
										<h3 className="text-sm font-semibold text-amber-800">Payment processing</h3>
										<p className="text-sm text-amber-700">{paymentError}</p>
									</div>
								</div>
							</div>
						)}

						{/* Success Message */}
						{showSuccessMessage && (
							<div className="mb-6 bg-green-50 border border-green-200 rounded-xl p-4">
								<div className="flex items-center gap-3">
									<div className="shrink-0 w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
										<svg className="w-5 h-5 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
											<path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
										</svg>
									</div>
									<div>
										<h3 className="text-sm font-semibold text-green-800">Payment successful!</h3>
										<p className="text-sm text-green-700">Thank you for subscribing. Your account has been upgraded.</p>
									</div>
								</div>
							</div>
						)}

						{/* Page Header */}
						<div className="mb-8">
							<h1 className="text-xl font-semibold text-primary">Billing</h1>
							<p className="mt-1 text-sm text-tertiary">Manage your subscription and billing information</p>
						</div>

						{/* Content based on subscription status */}
						{(subscriptionStatus === "trial_active" || subscriptionStatus === "trial_expired") && currentWorkspaceId && (
							<BillingTrialState
								trialEndsAt={selectedBrand?.trial_ends_at}
								isExpired={subscriptionStatus === "trial_expired"}
								workspaceId={currentWorkspaceId}
								partner={selectedBrand?.partner}
							/>
						)}

						{subscriptionStatus == "free_plan" && currentWorkspaceId && <BillingFreeState planDetails={planDetails} workspaceId={currentWorkspaceId} />}

						{subscriptionStatus === "subscription_active" && currentWorkspaceId && (
							<BillingActiveState
								planDetails={planDetails}
								subscriptionStartsAt={selectedBrand?.subscription_starts_at}
								subscriptionRenewsAt={selectedBrand?.subscription_renews_at}
								workspaceId={currentWorkspaceId}
							/>
						)}

						{subscriptionStatus === "subscription_lifetime" && currentWorkspaceId && (
							<BillingLifetimeState planDetails={planDetails} subscriptionStartsAt={selectedBrand?.subscription_starts_at} workspaceId={currentWorkspaceId} />
						)}
					</>
				)}
			</Container>
		</div>
	);
}
